import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = [
        "list",
        "target",
        "zoneRow",
        "itemTitle",
        "arrow",
        "clearButton"
    ]

    static values = {
        expanded: { type: Boolean, default: false },
        emptyLabel: { type: String, default: "All Zones" },
        showClearButton: { type: Boolean, default: false }
    }

    connect() {
        this.syncClearButtonVisibility()
    }

    toggle(event) {
        this.expandedValue = !this.expandedValue;
    }

    closeOnOutsideClick(event) {
        if (this.element.contains(event.target)) return;
        this.expandedValue = false;
    }

    handleCheckboxSelect(event) {
        const { zoneFilter, lessonFilter, schoolToken, domId } = this.element.dataset

        if (lessonFilter == "true") {
            const selectedStatuses = this.zoneRowTargets
                .filter((row) => row.checked)
                .map((row) => row.dataset.zoneName)

            document.querySelectorAll('[data-lesson-status]').forEach((lessonRow) => {
                const rowLabels = [lessonRow.dataset.lessonStatus, ...(lessonRow.dataset.lessonType || "").split(",")]
                const matches = selectedStatuses.length === 0 || rowLabels.some((label) => selectedStatuses.includes(label))
                lessonRow.style.display = matches ? "" : "none"
            })

            this.syncClearButtonVisibility()
            return;
        }

        const selectedRows = this.zoneRowTargets.filter((row) => row.checked)
        const totalZones = this.zoneRowTargets.length

        if (selectedRows.length === 0 || selectedRows.length === totalZones) {
            this.resetItemTitle()
        } else if (selectedRows.length === 1) {
            this.itemTitleTarget.textContent = selectedRows[0].dataset.zoneName || "1 zone selected"
        } else {
            this.updateItemTitle(selectedRows.length)
        }

        this.syncClearButtonVisibility()
    }

    clearSelection(event) {
        event.preventDefault();
        event.stopPropagation();

        const checkedRows = this.zoneRowTargets.filter((row) => row.checked)
        if (checkedRows.length === 0) return;

        const trigger = checkedRows[0]
        this.zoneRowTargets.forEach((row) => { row.checked = false })
        this.resetItemTitle()
        this.syncClearButtonVisibility()
        this.expandedValue = false

        // Refresh the calendar immediately instead of through the checkbox's
        // debounced change path: clearing is a single deliberate action, so there's
        // no burst of toggles to coalesce and the 1s debounce just feels laggy.
        // Call the navigation controller's zone update directly — the dataset it
        // reads (school token, dom id) lives on the checkbox, and getSelectedZoneIds
        // reads the live DOM, which is now empty. Collapsing the dropdown before the
        // refresh keeps it closed, since that path carries the expanded value onto
        // the incoming fragment. Fall back to a change event if the controller is
        // somehow unavailable.
        const navElement = trigger.closest('[data-controller~="schools-calendar-navigation"]')
        const navController = navElement && this.application.getControllerForElementAndIdentifier(navElement, "schools-calendar-navigation")
        if (navController) {
            navController.updateZone(trigger)
        } else {
            trigger.dispatchEvent(new Event("change", { bubbles: true }))
        }
    }

    syncClearButtonVisibility() {
        if (!this.hasClearButtonTarget) return;

        const hasSelection = this.zoneRowTargets.some((row) => row.checked)
        this.clearButtonTarget.classList.toggle("u-hidden", !hasSelection || !this.showClearButtonValue)
    }

    updateItemTitle(totalSelected) {
        this.itemTitleTarget.textContent = `${totalSelected} zones selected`
    }

    resetItemTitle() {
        this.itemTitleTarget.textContent = this.emptyLabelValue
    }

    expandedValueChanged(isExpanded) {
        if (!this.hasListTarget) return;

        if (isExpanded) {
        this.listTarget.style.transition = "height 0.06s ease-in-out";

        const height = this.listTarget.scrollHeight;
        this.listTarget.style.height = `${height}px`;

        const onEnd = () => {
            this.listTarget.removeEventListener("transitionend", onEnd);
            if (this.expandedValue) {
            this.listTarget.style.height = "auto";
            }
        };
        this.listTarget.addEventListener("transitionend", onEnd);

        if (this.hasArrowTarget) this.arrowTarget.style.transform = "rotate(0deg)";
        } else {
        if (this.listTarget.style.height === "auto") {
            this.listTarget.style.transition = "none";
            this.listTarget.style.height = `${this.listTarget.scrollHeight}px`;
            this.listTarget.offsetHeight;
        }

        this.listTarget.style.transition = "height 0.2s";
        this.listTarget.style.height = "0px";
        if (this.hasArrowTarget) this.arrowTarget.style.transform = "rotate(180deg)";
        }
  }
}
